import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import BandProfile from "@/pages/BandProfile";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import PlaylistDetail from "@/pages/PlaylistDetail";
import BandApply from "@/pages/BandApply";
import BandDashboard from "@/pages/BandDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BlogPost from "@/pages/BlogPost";
import Layout from "@/components/Layout";

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { processGoogleSession } = useAuth();
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      processGoogleSession(sessionId)
        .then((data) => {
          navigate('/', { replace: true, state: { user: data.user } });
        })
        .catch((error) => {
          console.error('Google auth failed:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate, processGoogleSession]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse text-primary font-accent text-2xl mb-4">AUTHENTICATING...</div>
        <div className="text-muted-foreground font-mono text-sm">Please wait</div>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, bandOnly = false }) => {
  const { user, loading, isAdmin, isBand } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location } });
    }
    if (!loading && adminOnly && !isAdmin) {
      navigate('/');
    }
    if (!loading && bandOnly && !isBand && !isAdmin) {
      navigate('/');
    }
  }, [loading, user, isAdmin, isBand, adminOnly, bandOnly, navigate, location]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-2xl">LOADING...</div>
      </div>
    );
  }
  
  if (!user) return null;
  if (adminOnly && !isAdmin) return null;
  if (bandOnly && !isBand && !isAdmin) return null;
  
  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/band/:bandId" element={<BandProfile />} />
        <Route path="/blog/:postId" element={<BlogPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/apply" element={
          <ProtectedRoute>
            <BandApply />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/playlist/:playlistId" element={
          <ProtectedRoute>
            <PlaylistDetail />
          </ProtectedRoute>
        } />
        <Route path="/band-dashboard" element={
          <ProtectedRoute bandOnly>
            <BandDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#111',
              border: '1px solid #333',
              color: '#e5e5e5',
              fontFamily: 'DM Mono, monospace',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
